import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { IFilter } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import {
  aggregateCountGroups,
  getGroups,
  getGroupsByAggregate,
  getGroupMemberPermissions,
  getGroupMembers,
  getNumberOfGroupMember,
  getGroupById
} from 'API/groups'
import { IGroup, IGroupMember, IGroupMemberDetail, IGroupMemberPermission } from 'interfaces/group'

class GroupStore {
  rootStore: RootStore

  groups: IGroup[] = []
  groupDetail: IGroup | undefined = undefined
  groupMemberPermissions: IGroupMemberPermission[] = []
  groupMembers: IGroupMember[] = []
  groupMembersDetail: IGroupMemberDetail[] = []
  numberOfGroupMembers: number = 0
  totalGroupsCount: number = 0
  isLoading: boolean = false
  isManageModeInMemberList: boolean = false
  isManageModeInGroupList: boolean = false

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public async getGroups(filter: IFilter<IGroup> = {}): Promise<void> {
    this.isLoading = true
    this.groups = await getGroups(filter)
    this.isLoading = false
  }

  public setManageModeInMemberList(isManageMode: boolean) {
    this.isManageModeInMemberList = isManageMode ?? false
  }

  public setManageModeInGroupList(isManageMode: boolean) {
    this.isManageModeInGroupList = isManageMode ?? false
  }

  public async getGroupDetail(id: number): Promise<void> {
    this.isLoading = true
    this.groupDetail = await getGroupById(id)
    this.isLoading = false
  }

  public async getGroupListByAggregate(
    pipeline: AggregationPipeline = [],
    countPipeline: AggregationPipeline = [],
    orgIds?: number[]
  ): Promise<void> {
    this.isLoading = true
    const dataResponse = await getGroupsByAggregate(pipeline, orgIds)
    const countResponse = await aggregateCountGroups(countPipeline, orgIds)
    this.groups = dataResponse
    this.totalGroupsCount = countResponse
    this.isLoading = false
  }

  public async getGroupMemberPermissions(filter: IFilter<IGroupMemberPermission> = {}): Promise<void> {
    this.isLoading = true
    this.groupMemberPermissions = await getGroupMemberPermissions(filter)
    this.isLoading = false
  }

  public async getGroupMembers(id: number, filter: IFilter<IGroup> = {}, orgId?: number): Promise<void> {
    this.isLoading = true
    const { paginatedResult = [], totalCount = 0 } = await getGroupMembers(id, filter, orgId)
    this.groupMembersDetail = paginatedResult
    this.numberOfGroupMembers = totalCount
    this.isLoading = false
  }

  public setGroupMembers(groupMembers: IGroupMember[]): void {
    this.groupMembers = groupMembers
  }

  public async getNumberOfGroupMembers(id: number): Promise<void> {
    this.isLoading = true
    this.numberOfGroupMembers = await getNumberOfGroupMember(id)
    this.isLoading = false
  }

  public resetStore(): void {
    this.groups = []
    this.groupDetail = undefined
    this.groupMemberPermissions = []
    this.groupMembers = []
    this.groupMembersDetail = []
    this.numberOfGroupMembers = 0
    this.totalGroupsCount = 0
    this.isLoading = false
    this.isManageModeInGroupList = false
  }
}

export default GroupStore
